import {
  DENETIM_DURUM_ADI,
  DENETIM_TURLERI,
  DENETIM_TUR_ADI,
  denetimTarihHatasi,
  type DenetimDurumu,
  type DenetimSayimi,
  type DenetimTuru,
  type DenetimTurSayimi,
  type KarsilastirmaNotu,
  type NdtNotu,
  type PwhtNotu,
  type WeldMapNotu,
} from "@oasis/domain";
import { cevir } from "@/lib/dil";
import { ndtNotMetni, notMetni, pwhtNotMetni, weldMapNotMetni } from "@/lib/belgeler";

/**
 * D-074 Faz 2 · PR 12 (K19): Documents › Audit görünümünün saf yardımcıları (prototip belgeler--denetim). Yol kurucuları, durum adları ve tonları, toplu
 * sıranın sayım satırları, koşu cümlesi ve türün karşılaştırma notlarının metni (türün bugünkü not fonksiyonu).
 */

export const DENETIM_UCU = {
  liste: "/belge-denetimi",
  sayi: "/belge-denetimi/sayi",
  sira: "/belge-denetimi/sira",
  ayrinti: (id: string) => `/belge-denetimi/${id}`,
  cikar: (id: string) => `/belge-denetimi/${id}/cikar`,
  goruldu: (uyariId: string) => `/belge-denetimi/uyari/${uyariId}/goruldu`,
} as const;

/** Liste yolu: tür, durum, açık bulgu, arama ve imleç (boş olanlar yazılmaz). */
export function denetimListeYolu(q: { tur?: DenetimTuru | null; durum?: DenetimDurumu | null; acik?: boolean; ara?: string; once?: string | null } = {}): string {
  const p = new URLSearchParams();
  if (q.tur) p.set("tur", q.tur);
  if (q.durum) p.set("durum", q.durum);
  if (q.acik) p.set("acik", "1");
  if (q.ara?.trim()) p.set("ara", q.ara.trim());
  if (q.once) p.set("once", q.once);
  const s = p.toString();
  return s ? `${DENETIM_UCU.liste}?${s}` : DENETIM_UCU.liste;
}

export const durumAdi = (d: DenetimDurumu): string => cevir(DENETIM_DURUM_ADI[d]);
/** Durum hapının tonu: okundu yeşil, okunamadı kırmızı, öteki gri. */
export const durumTonu = (d: DenetimDurumu): "ok" | "bad" | "neutral" => (d === "okundu" ? "ok" : d === "hata" ? "bad" : "neutral");
export const turAdi = (t: string): string => cevir(DENETIM_TUR_ADI[t as DenetimTuru] ?? t);

/** Türün okuma sözleşmesinin sürümü (docs/hafif-excel-sozlesmesi.md; denetim aynı sözleşmeyi kullanır). */
export const SOZLESME_SURUMU: Record<DenetimTuru, number> = { hidrotest: 2, ndt: 2, weldmap: 1, pwht: 1 };
export const sozlesmeMetni = (t: DenetimTuru): string => cevir("{0}, version {1} (the same fields as a normal reading)", turAdi(t), SOZLESME_SURUMU[t]);

/** Sayım tablosunun "Not read again" hücresi: sıfır olmayan parçalar " · " ile; ikisi de sıfırsa "0". */
export function okunmayanMetni(s: Pick<DenetimTurSayimi, "atlandi" | "documents">): string {
  const p: string[] = [];
  if (s.atlandi) p.push(cevir("{0} already audited or queued", s.atlandi));
  if (s.documents) p.push(cevir("{0} read in Documents", s.documents));
  return p.length ? p.join(" · ") : "0";
}

/** Sayım tablosunun satırları: yalnız aday bulunan türler (seçilen türler sayımda hep görünür), sonra Total. */
export function sayimSatirlari(s: DenetimSayimi, secilen: readonly DenetimTuru[]): { ad: string; bulunan: number; okunmayan: string; okunacak: number; toplam?: true }[] {
  const satirlar = DENETIM_TURLERI.filter((t) => secilen.includes(t) || s.turler[t].aday > 0).map((t) => {
    const x = s.turler[t];
    return { ad: turAdi(t), bulunan: x.aday, okunmayan: okunmayanMetni(x), okunacak: x.siraya + x.kopya + x.bekleyen };
  });
  return [...satirlar, { ad: cevir("Total"), bulunan: s.toplam.aday, okunmayan: String(s.toplam.atlandi), okunacak: s.toplam.alinacak, toplam: true }];
}

/** Sayımın açıklaması ("Of the {0}: …") ve, varsa, kalanlar ile kuyruğun doluluğu. */
export function kosuMetni(s: DenetimSayimi): string[] {
  const t = DENETIM_TURLERI.reduce((a, x) => ({ siraya: a.siraya + s.turler[x].siraya, kopya: a.kopya + s.turler[x].kopya, bekleyen: a.bekleyen + s.turler[x].bekleyen }), { siraya: 0, kopya: 0, bekleyen: 0 });
  const l = [cevir("Of the {0}: {1} go to the reader, {2} take the reading of the same file already read, {3} wait for the same file in the queue. At most {4} documents per run.", s.toplam.alinacak, t.siraya, t.kopya, t.bekleyen, s.sinir)];
  if (s.toplam.kalan > 0) l.push(cevir("{0} more remain; run again for the rest.", s.toplam.kalan));
  if (s.kuyrukta > 0) l.push(cevir("The audit queue holds {0} of {1} documents.", s.kuyrukta, s.kuyrukSiniri));
  return l;
}

/** Gerçek koşunun sonucu. */
export function sonucMetni(s: DenetimSayimi): string {
  const t = DENETIM_TURLERI.reduce((a, x) => ({ siraya: a.siraya + s.turler[x].siraya, kopya: a.kopya + s.turler[x].kopya, bekleyen: a.bekleyen + s.turler[x].bekleyen }), { siraya: 0, kopya: 0, bekleyen: 0 });
  return cevir("{0} queued, {1} read once from the same file, {2} waiting for the same file.", t.siraya, t.kopya, t.bekleyen);
}

/** Tarih kutularının ön denetimi (sunucuyla aynı kural); hata cümlesi çevrilmiş ya da null. */
export function tarihHatasi(bas: string, son: string): string | null {
  const h = denetimTarihHatasi(bas || null, son || null);
  return h ? cevir(h) : null;
}

/** Sayımın süzgeç imzası: "Queue {0} documents" yalnız son sayım aynı süzgeçle yapıldıysa açık. */
export const sayimImzasi = (turler: readonly DenetimTuru[], bas: string, son: string): string => JSON.stringify([[...turler].sort(), bas || null, son || null]);

/** Karşılaştırma notunun metni: türün bugünkü not fonksiyonu (Documents kartıyla aynı cümle). */
export function denetimNotMetni(tur: DenetimTuru, n: unknown): string {
  switch (tur) {
    case "hidrotest": return notMetni(n as KarsilastirmaNotu);
    case "ndt": return ndtNotMetni(n as NdtNotu);
    case "weldmap": return weldMapNotMetni(n as WeldMapNotu);
    case "pwht": return pwhtNotMetni(n as PwhtNotu);
  }
}

/**
 * Konumun "#" bölümünün Documents görünümüne etkisi: "#denetim" Audit görünümünü açar; "#uyarilar" (Alerts) ve "#belge-<id>" (satırın kartı) Audit'i
 * kapatır, yoksa Audit açıkken zil bağlantısının kartı görünmezdi. Başka bölüm: null (hiçbir şey değişmez).
 */
export function hashGorunumu(hash: string | null | undefined): { denetimAcik: boolean; uyarilar?: true; acikId?: string } | null {
  const h = String(hash ?? "").replace(/^#/, "");
  if (h === "denetim") return { denetimAcik: true };
  if (h === "uyarilar") return { denetimAcik: false, uyarilar: true };
  if (h.startsWith("belge-") && h.length > "belge-".length) return { denetimAcik: false, acikId: h.slice("belge-".length) };
  return null;
}
